/**
 * Admin Items Page
 * View and manage menu items
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  LayoutGrid,
  List,
  Camera,
  Download,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { OptimizationChip } from '@/components/OptimizationBadge';
import { itemService } from '@/services/itemService';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { Item } from '@/types/item';
import { formatPrice } from '@/lib/demoPrice';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';
import { CatalogImportDialog } from '@/components/admin/dialogs/CatalogImportDialog';
import {
  compareCategory,
  countByStatus,
  groupMenuItems,
  MENU_STATUS_LABELS,
  type MenuStatus,
} from '@/lib/menuOrder';

type StatusFilter = MenuStatus | '';

const GROUP_TITLES: Record<MenuStatus, string> = {
  new: 'Nuevos · pendientes de foto',
  active: 'Activos',
  inactive: 'Inactivos',
};

export function ItemsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackAdminItem } = useAnalytics();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [catalogOpen, setCatalogOpen] = useState(false);

  // Fetch items
  const {
    data: items = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin-items', categoryFilter],
    queryFn: () => itemService.getItems({
      category: categoryFilter || undefined,
    }),
  });

  // Delete item mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => itemService.deleteItem(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      trackAdminItem.delete(id);
      toast.success('Item eliminado correctamente');
    },
    onError: () => {
      toast.error('Error al eliminar el item');
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      itemService.toggleActive(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      toast.success('Estado actualizado correctamente');
    },
    onError: () => {
      toast.error('Error al actualizar el estado');
    },
  });

  /**
   * Handle delete item
   */
  const handleDelete = (item: Item) => {
    if (window.confirm(`¿Estás seguro de eliminar "${item.name}"?`)) {
      deleteMutation.mutate(item.id);
    }
  };

  /**
   * Handle toggle active
   */
  const handleToggleActive = (item: Item) => {
    toggleActiveMutation.mutate({
      id: item.id,
      active: !item.active,
    });
  };

  /**
   * Filter items by search query
   */
  const searchedItems = items.filter((item) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.category?.toLowerCase().includes(searchLower)
    );
  });

  /**
   * Status counts (after search and category) and blocks: new, active, inactive,
   * each sorted by course and name (spec 010-menu-platos-tpv)
   */
  const statusCounts = countByStatus(searchedItems);
  const groups = groupMenuItems(searchedItems).filter(
    (group) => group.items.length > 0 && (!statusFilter || group.status === statusFilter)
  );
  const visibleCount = groups.reduce((sum, group) => sum + group.items.length, 0);

  /**
   * Get unique categories, in course order
   */
  const categories = useMemo(
    () =>
      Array.from(new Set(items.map((item) => item.category).filter((c): c is string => Boolean(c)))).sort(
        compareCategory
      ),
    [items]
  );

  if (isLoading) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Menú" />
          <PageLoading variant="grid" rows={2} columns={4} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Menú"
          subtitle={`${visibleCount} ${visibleCount === 1 ? 'plato' : 'platos'}`}
        >
          <AdminButton
            variant="primary"
            onClick={() => setCatalogOpen(true)}
            className="min-h-11"
          >
            <Download className="mr-2 h-4 w-4" />
            Añadir desde el TPV
          </AdminButton>
          <AdminButton
            onClick={() => refetch()}
            isLoading={isLoading}
            className="min-h-11"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </AdminButton>
        </PageHeader>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-content-secondary" />
            <Input
              placeholder="Buscar items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="min-h-11 pl-10 border-content-primary focus-visible:ring-content-primary font-light"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="min-h-11 px-3 text-sm border border-gray-300 rounded-sm bg-white font-light text-[#1e1e1e] focus:outline-none focus:ring-1 focus:ring-[#1e1e1e]"
          >
            <option value="">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Filtrar por estado"
            className="min-h-11 px-3 text-sm border border-gray-300 rounded-sm bg-white font-light text-[#1e1e1e] focus:outline-none focus:ring-1 focus:ring-[#1e1e1e]"
          >
            <option value="">Todos los estados ({statusCounts.new + statusCounts.active + statusCounts.inactive})</option>
            {(['new', 'active', 'inactive'] as MenuStatus[]).map((status) => (
              <option key={status} value={status}>
                {MENU_STATUS_LABELS[status]} ({statusCounts[status]})
              </option>
            ))}
          </select>
          <div className="flex border border-gray-300 rounded-sm overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 min-h-11 ${viewMode === 'grid' ? 'bg-[#1e1e1e] text-white' : 'bg-white text-[#1e1e1e] hover:bg-gray-50'}`}
              title="Vista cuadrícula"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 min-h-11 ${viewMode === 'list' ? 'bg-[#1e1e1e] text-white' : 'bg-white text-[#1e1e1e] hover:bg-gray-50'}`}
              title="Vista lista"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          </div>

        {/* Items, in blocks: new, active, inactive */}
        {visibleCount > 0 ? (
          <div className="space-y-8">
          {groups.map((group) => (
          <section key={group.status} aria-label={GROUP_TITLES[group.status]} className="space-y-3">
            <h2 className="flex items-baseline gap-2 text-[13px] font-medium uppercase tracking-wider text-[#1e1e1e]">
              {GROUP_TITLES[group.status]}
              <span className="font-light text-[#9d9c9c]">{group.items.length}</span>
            </h2>
          {viewMode === 'grid' ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {group.items.map((item) => (
              <div key={item.id} className="bg-white border border-gray-200 rounded overflow-hidden hover:shadow-sm transition-shadow">
                {/* Image */}
                <div className="relative aspect-[9/16] bg-gray-100">
                  {/* Optimization Chip for first photo */}
                  {item.photos.length > 0 && item.photos[0].optimization_status && item.photos[0].content_type?.startsWith('image/') && (
                    <div className="absolute top-2 left-2 z-10">
                      <OptimizationChip status={item.photos[0].optimization_status} />
                    </div>
                  )}
                  {item.photos.length > 0 ? (
                    item.photos[0].content_type?.startsWith('video/') ? (
                      <div className="w-full h-full relative">
                        <video
                          src={item.photos[0].url}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                        />
                        <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded shadow-lg flex items-center gap-1">
                          VIDEO
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.photos[0].url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/items/${item.id}/edit`)}
                      className="w-full h-full flex flex-col items-center justify-center text-content-secondary font-light text-sm gap-2 hover:bg-orange-50 transition-colors"
                    >
                      <Camera className="w-12 h-12 text-orange-400" strokeWidth={1.5} />
                      <span className="text-orange-600 font-medium">Añadir foto</span>
                    </button>
                  )}
                  {!item.active && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="bg-white text-content-primary px-3 py-1 text-xs font-light uppercase tracking-wider">
                        Inactivo
                      </span>
                    </div>
                  )}
                  {item.photos.length === 0 && item.active && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-white bg-orange-600 uppercase tracking-wider px-2 py-1 rounded shadow-sm">
                        Sin foto
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-base font-light text-content-primary line-clamp-1">{item.name}</h3>
                    <span className="text-base font-light text-content-primary whitespace-nowrap">
                      {formatPrice(item.price_cents, item.currency)}
                    </span>
                  </div>

                  {item.category && (
                    <span className="inline-block text-xs font-medium text-content-primary uppercase tracking-wider border border-gray-200 px-2 py-1 rounded mb-2">
                      {item.category}
                    </span>
                  )}

                  {item.description && (
                    <p className="text-xs font-light text-content-secondary line-clamp-2 mb-4">
                      {item.description}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <AdminButton
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => navigate(`/admin/items/${item.id}/edit`)}
                    >
                      <Edit className="mr-1 h-3 w-3" />
                      Editar
                    </AdminButton>
                    <AdminButton
                      size="sm"
                      onClick={() => handleToggleActive(item)}
                      disabled={toggleActiveMutation.isPending}
                    >
                      {item.active ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </AdminButton>
                    <AdminButton
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(item)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </AdminButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div className="bg-white border border-gray-200 divide-y divide-gray-100">
              {group.items.map((item) => (
                <div key={item.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="h-10 w-10 rounded-sm bg-gray-100 overflow-hidden shrink-0">
                    {item.photos.length > 0 ? (
                      <img src={item.photos[0].url} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/items/${item.id}/edit`)}
                        className="w-full h-full flex items-center justify-center text-orange-400 hover:bg-orange-50"
                        aria-label={`Añadir foto a ${item.name}`}
                      >
                        <Camera className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-light text-[#1e1e1e] truncate">{item.name}</span>
                      {!item.active && (
                        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-100 px-1.5 py-0.5 rounded-sm">Inactivo</span>
                      )}
                      {item.active && item.photos.length === 0 && (
                        <span className="text-[10px] font-medium text-white uppercase tracking-wider bg-orange-600 px-1.5 py-0.5 rounded-sm">Sin foto</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {item.category && (
                        <span className="text-[11px] font-light text-[#9d9c9c] uppercase tracking-wider">{item.category}</span>
                      )}
                      {item.photos.length > 0 && item.photos[0].optimization_status && item.photos[0].content_type?.startsWith('image/') && (
                        <OptimizationChip status={item.photos[0].optimization_status} />
                      )}
                    </div>
                  </div>
                  <span className="text-[14px] font-light text-[#1e1e1e] whitespace-nowrap">
                    {formatPrice(item.price_cents, item.currency)}
                  </span>
                  <div className="flex items-center gap-1">
                    <AdminButton size="sm" onClick={() => navigate(`/admin/items/${item.id}/edit`)}>
                      <Edit className="h-3.5 w-3.5" />
                    </AdminButton>
                    <AdminButton size="sm" onClick={() => handleToggleActive(item)} disabled={toggleActiveMutation.isPending}>
                      {item.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </AdminButton>
                    <AdminButton size="sm" variant="danger" onClick={() => handleDelete(item)} disabled={deleteMutation.isPending}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </AdminButton>
                  </div>
                </div>
              ))}
            </div>
          )}
          </section>
          ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded p-12 text-center">
            <p className="text-sm font-light text-content-secondary">
              {searchQuery || categoryFilter || statusFilter
                ? 'No hay platos con los filtros aplicados.'
                : 'Aún no hay platos en el menú. Pulsa «Añadir desde el TPV» para traer los de tu carta.'}
            </p>
          </div>
        )}

        <CatalogImportDialog
          open={catalogOpen}
          onOpenChange={setCatalogOpen}
          onImported={(result) => {
            if (result.created.length > 0) setStatusFilter('new');
          }}
        />
      </div>
    </AdminPageShell>
  );
}
